import type { Action, Location, TransitionPromptHook } from "history";
import { createBrowserHistory } from "history";

const history = createBrowserHistory<{ blockable?: boolean }>();
const historyBlockers: TransitionPromptHook[] = [];
type BeforeNavigationListener = (location: Location, action: Action) => void;
const beforeNavigationListeners: BeforeNavigationListener[] = [];
let lastBeforeNavigationSignature: string | null = null;
let clearBeforeNavigationSignatureTimeoutId: number | null = null;

function getLocationSignature(location: Pick<Location, "pathname" | "search" | "hash">): string {
	return `${location.pathname}${location.search}${location.hash}`;
}

function emitBeforeNavigation(location: Location, action: Action): void {
	const signature = `${action}:${getLocationSignature(location)}`;
	if (lastBeforeNavigationSignature === signature) {
		return;
	}
	lastBeforeNavigationSignature = signature;
	beforeNavigationListeners.forEach(listener => {
		listener(location, action);
	});
}

history.block((location, action) => {
	if (!(location.state && location.state.blockable === false)) {
		for (const blocker of historyBlockers) {
			const res = blocker(location, action);
			if (res) {
				return res;
			}
		}
	}

	emitBeforeNavigation(location, action);

	return undefined;
});

export const addBlocker = (blocker: TransitionPromptHook) => {
	historyBlockers.push(blocker);
	return () => {
		const index = historyBlockers.indexOf(blocker);
		if (index > -1) {
			historyBlockers.splice(index, 1);
		}
	};
};

export const addBeforeNavigationListener = (listener: BeforeNavigationListener) => {
	beforeNavigationListeners.push(listener);
	return () => {
		const index = beforeNavigationListeners.indexOf(listener);
		if (index > -1) {
			beforeNavigationListeners.splice(index, 1);
		}
	};
};

if (typeof window !== "undefined") {
	window.addEventListener("popstate", () => {
		emitBeforeNavigation(history.location, "POP");
	});
}

history.listen(location => {
	if (clearBeforeNavigationSignatureTimeoutId !== null) {
		window.clearTimeout(clearBeforeNavigationSignatureTimeoutId);
	}
	clearBeforeNavigationSignatureTimeoutId = window.setTimeout(() => {
		lastBeforeNavigationSignature = null;
		clearBeforeNavigationSignatureTimeoutId = null;
	}, 0);
	// A comment in the django template base.html points to this code, if moving this code please update that.
	const storeLink = document.getElementById("ios-app-store-link");
	if (storeLink) {
		let content = storeLink.getAttribute("content");
		if (content) {
			content = content.replace(/app-argument=.*?(,|$)/, `app-argument=${location.pathname}$1`);
			storeLink.setAttribute("content", content);
		}
	}
});
export default history;
